/*** Swarm RTS AI module - (c) mjh.at - v0.3.2 2014-12-28 ***/

import { Util } from './util.js';
import { Faction, factionId, factionFromId } from './factions.js';

/**
 * Spiral-outward grid search: finds the nearest living enemy unit to `fromUnit`.
 * Expands ring by ring; once a candidate is found at ring N, only ring N+1 needs
 * checking to confirm it is truly the closest — then stops. Much faster than a
 * linear scan of all units when enemies are nearby.
 *
 * @param {object} fromUnit - Entity whose position is the search origin
 * @param {import('./util.js').GridSystem} grid
 * @param {number} myFaction
 * @returns {object|null} The nearest enemy entity, or null if none exists
 */
function findNearestEnemy(fromUnit, grid, myFaction) {
    const gridSize = grid.gridSize;
    const gridsX = grid.gridsX;
    const gridsY = grid.gridsY;
    const sectors = grid.sectors;
    const sx = grid.clampSectorX(fromUnit.x);
    const sy = grid.clampSectorY(fromUnit.y);
    const maxRing = Math.max(gridsX, gridsY);

    let bestDistSq = Infinity;
    let bestEnemy = null;

    for (let ring = 0; ring <= maxRing; ring++) {
        // Early exit: inner edge of this ring is already farther than best found so far.
        if (ring >= 2) {
            const innerEdgeDist = (ring - 1) * gridSize;
            if (innerEdgeDist * innerEdgeDist > bestDistSq) break;
        }

        for (let odx = -ring; odx <= ring; odx++) {
            for (let ody = -ring; ody <= ring; ody++) {
                if (ring > 0 && Math.abs(odx) !== ring && Math.abs(ody) !== ring) continue;
                const gx = sx + odx;
                const gy = sy + ody;
                if (gx < 0 || gx >= gridsX || gy < 0 || gy >= gridsY) continue;

                const sector = sectors[gx][gy];
                for (let i = 0; i < sector.entities.length; i += 1) {
                    const e = sector.entities[i];
                    if (e.faction === myFaction || e.faction === Faction.NEUTRAL) continue;
                    if (!e.health || e.health.isDestroyed()) continue;
                    const dSq = Util.distSq(fromUnit.x, fromUnit.y, e.x, e.y);
                    if (dSq < bestDistSq) {
                        bestDistSq = dSq;
                        bestEnemy = e;
                    }
                }
            }
        }
    }

    return bestEnemy;
}

/** @typedef {{Defend: number, Maintain: number, Attack: number}} Strategy */
const Strategy = {
    Defend: 10,
    Maintain: 20,
    Attack: 30
};

const AI_DEBUG = false;
const MOVING_SPAWNER_LEAD_TIME_SEC = 1;

/**
 * Base AI controller: owns all per-unit tactical logic (sendUnits, engagement,
 * objective grabbing) and the spawner analysis state that the strategic layer
 * writes into. Strategic planning is delegated to runStrategicPlan(), which
 * subclasses override.
 */
class TacticalAi {

    /**
     * @param {number} faction - Faction ID (1 or 2)
     * @param {import('./util.js').EntitySystem} entitySystem
     * @param {import('./util.js').GridSystem|null} [locationGrid] - Spatial index for O(1) queries
     */
    constructor(faction, entitySystem, locationGrid = null) {
        this.factionState = factionFromId(faction);
        /** @type {number} */
        this.faction = factionId(faction);
        /** @type {EntitySystem} */
        this.entitySystem = entitySystem;
        /** @type {import('./util.js').GridSystem|null} Spatial index for O(1) radius queries. */
        this.locationGrid = locationGrid;
        /** @type {string} Identifies the concrete AI type for diagnostics. */
        this.aiTypeName = 'heuristic';

        /** @type {number} */
        this.time = 0;
        /** @type {number} */
        this.planRate = 2; // every 2 seconds

        /** @type {number} */
        this.nextPlanTime = Math.random() * this.planRate;

        /** @type {number} */
        this.strategy = Strategy.Attack;

        /** @type {Array} */
        this.spawnerAnalysis = [];
        /** @type {Array} */
        this.spawnerDistances = [];

        /** @type {number} */
        this.prioritySum = 0;
        /** @type {number} */
        this.unitBalance = 1;

        /** @type {number} */
        this.maxDesignations = 1;

        /** @type {Entity[]} Neutral capturable entities (derelict ships, pickups) */
        this.neutralObjectives = [];

        /**
         * Snapshot of this faction's units captured at the start of a plan cycle.
         * Drained progressively over the cycle by `_drainSendQueue` to amortize
         * what was previously a single O(U) burst into per-frame slices.
         * @type {Array|null}
         */
        this._sendQueue = null;
        /** @type {number} Index of next unit to process in _sendQueue. */
        this._sendCursor = 0;
        /**
         * Spatial precompute (e.g. enemyNearbyBySpawner) captured at plan tick
         * and reused for every unit drained from _sendQueue this cycle.
         * @type {{enemyNearbyBySpawner: boolean[]}|null}
         */
        this._sendPrecompute = null;
        /** @type {number} `this.time` value at which the current send cycle should be fully drained. */
        this._sendCycleEndTime = 0;

        /** @type {Uint16Array|null} Flat [sx*gridsY+sy] → min Manhattan hop distance to nearest enemy sector. */
        this._nearestEnemyDist = null;
        /** @type {Array<import('./util.js').Sector|null>|null} Flat [sx*gridsY+sy] → nearest enemy sector for this faction. */
        this._nearestEnemySectors = null;
    }

    /**
     * @param {Entity} unit
     * @param {string} state
     */
    setUnitAiDebugState(unit, state) {
        unit.aiDebugState = state;
    }

    /**
     * @param {Entity} unit
     * @returns {Entity|null}
     */
    getValidEngagementTarget(unit) {
        if (!unit || !unit.combat || !unit.combat.target) {
            return null;
        }
        const target = unit.combat.target;
        if (!target.health || target.health.isDestroyed()) {
            unit.combat.target = null;
            this.setUnitAiDebugState(unit, "stale_target");
            return null;
        }
        if (target.faction === unit.faction || target.faction === 0) {
            unit.combat.target = null;
            this.setUnitAiDebugState(unit, "stale_target");
            return null;
        }
        const attackRange = unit.combat.attackRange || 0;
        const maxEngageLeash = attackRange > 0 ? attackRange * 2.0 : 80;
        if (unit.distance(target) > maxEngageLeash) {
            unit.combat.target = null;
            this.setUnitAiDebugState(unit, "stale_target");
            return null;
        }
        return target;
    }

    /**
     * Lower score means "more urgent objective".
     * @param {Entity} spawner
     * @param {boolean} hasEnemyNearby
     * @returns {number}
     */
    getSpawnerObjectivePriority(spawner, hasEnemyNearby) {
        if (!spawner.isFullyCaptured()) {
            return 0;
        }
        if (spawner.capturedByFaction !== this.faction && spawner.capturedByFaction !== 0) {
            return 1;
        }
        if (spawner.capturedByFaction === this.faction && hasEnemyNearby) {
            return 2;
        }
        return 3;
    }

    /**
     * @param {Entity} spawner
     * @returns {boolean}
     */
    isSpawnerMoving(spawner) {
        return !!spawner.orbit &&
            (!spawner.orbit.onlyWhenNeutral || spawner.capturedByFaction === Faction.NEUTRAL);
    }

    /**
     * @param {Entity} spawner
     * @returns {{x:number, y:number}}
     */
    getSpawnerTargetPosition(spawner) {
        if (!this.isSpawnerMoving(spawner)) {
            return { x: spawner.x, y: spawner.y };
        }
        const orbit = spawner.orbit;
        const predictedAngle = orbit.angle + orbit.angularSpeed * MOVING_SPAWNER_LEAD_TIME_SEC;
        return {
            x: orbit.centerX + Math.cos(predictedAngle) * orbit.radius,
            y: orbit.centerY + Math.sin(predictedAngle) * orbit.radius
        };
    }

    init() {
        this.analyzeSpawners();

        // Specifies how many targets are chosen by the AI
        // Limit to 2-3 spawners to avoid spreading forces too thin
        this.maxDesignations = Math.min(3, Math.max(1, Math.floor((this.spawnerAnalysis.length - 1) / 4)));
    }

    buildNeutralObjectives() {
        this.neutralObjectives = [];
        const units = this.entitySystem.units;
        for (let i = 0; i < units.length; i++) {
            const e = units[i];
            if (e.capture && e.faction === Faction.NEUTRAL && !e.isFullyCaptured() && !e.isDestroyed()) {
                this.neutralObjectives.push(e);
            }
        }
    }

    analyzeSpawners() {
        var s, i, spawner, distance;

        this.spawnerAnalysis = [];
        this.spawnerDistances = [];

        // Find my HQ(s)
        for (s = 0; s < this.entitySystem.spawners.length; s += 1) {
            spawner = this.entitySystem.spawners[s];

            if (this.faction === spawner.capturedByFaction) {

                this.spawnerAnalysis.push({
                    distanceToHq: 0,
                    object: spawner,
                    sectors: spawner.getSectorsInRange(spawner.capture.captureRadius * 1.3),
                    priority: 0,
                    enemyDepth: 0,
                    unitsToSend: 0,
                    unitCounts: [0, 0]
                });
            }
        }

        if (this.spawnerAnalysis.length === 0) {
            console.error("No HQ found! What kind of game should that be?!");
            return;
        }

        // Find all other spawners
        for (s = 0; s < this.entitySystem.spawners.length; s += 1) {
            spawner = this.entitySystem.spawners[s];

            if (spawner !== this.spawnerAnalysis[0].object) {
                // distance from HQ
                distance = spawner.distance(this.spawnerAnalysis[0].object);
                this.spawnerAnalysis.push({
                    distanceToHq: distance,
                    object: spawner,
                    sectors: spawner.getSectorsInRange(spawner.capture.captureRadius * 1.5),
                    priority: 0,
                    enemyDepth: 0,
                    unitsToSend: 0,
                    unitCounts: [0, 0]
                });
            }
        }

        // Sort spawners by distance to HQ
        this.spawnerAnalysis.sort((s1, s2) => s1.distanceToHq - s2.distanceToHq);

        // Store distances between each spawner pair
        const sortByDistance = (s1, s2) => s1.distance - s2.distance;
        for (s = 0; s < this.spawnerAnalysis.length; s += 1) {
            this.spawnerDistances[s] = [];
            for (i = 0; i < this.spawnerAnalysis.length; i += 1) {
                this.spawnerDistances[s][i] = {
                    index: i,
                    distance: this.spawnerAnalysis[s].object.distance(this.spawnerAnalysis[i].object)
                };
            }
            this.spawnerDistances[s].sort(sortByDistance);
        }

        if (AI_DEBUG) {
            console.log("AI", this.faction, "spawnerAnalysis", this.spawnerAnalysis);
            console.log("AI", this.faction, "spawnerDistances", this.spawnerDistances);
        }
    }

    /**
     * Subclasses override this to perform strategic planning (determining
     * strategy and assigning spawner priorities). Called once per planRate.
     * Should populate this.strategy and this.spawnerAnalysis[*].priority/unitsToSend.
     */
    runStrategicPlan() {}

    /**
     * @param {number} timePassed
     */
    update(timePassed) {
        this.time += timePassed;

        if (this.time >= this.nextPlanTime) {
            this.nextPlanTime += this.planRate;

            this.buildNeutralObjectives();

            const t1 = performance.now();
            this.runStrategicPlan();
            const t2 = performance.now();
            this._beginSendCycle();
            const t3 = performance.now();

            // Store timings for game.js to collect.
            // lastSendUnitsTime now reflects only the precompute + snapshot cost
            // (per-unit work is amortized across frames via _drainSendQueue).
            this.lastStrategicPlanTime = t2 - t1;
            this.lastSendUnitsTime = t3 - t2;
        } else {
            this.lastStrategicPlanTime = 0;
            this.lastSendUnitsTime = 0;
        }

        this._drainSendQueue(timePassed);
    }

    /**
     * Normalized combat strength for a single unit — matches the capture system baseline.
     * Fighter avg damage (15) = 1.0.
     * @param {Entity} entity
     * @returns {number}
     */
    static unitStrength(entity) {
        if (!entity.combat) return 1;
        return (entity.combat.minDamage + entity.combat.maxDamage) / 30;
    }

    /**
     * Total strength-weighted unit count for a faction across all registered units.
     * @param {number} faction
     * @returns {number}
     */
    getGlobalStrength(faction) {
        let total = 0;
        const units = this.entitySystem.units;
        for (let i = 0; i < units.length; i++) {
            if (units[i].faction === faction) total += TacticalAi.unitStrength(units[i]);
        }
        return total;
    }

    /**
     * @param {Sector[]} sectors
     * @returns {[number, number]} [myStrength, enemyStrength] — strength-weighted, not raw counts
     */
    getUnitCount(sectors) {
        let myStr = 0, enemyStr = 0;
        for (let s = 0; s < sectors.length; s += 1) {
            const entities = sectors[s].entities;
            for (let i = 0; i < entities.length; i += 1) {
                const e = entities[i];
                if (!e.faction || e.faction === Faction.NEUTRAL) continue;
                if (!e.isCombatShip || !e.isCombatShip()) continue;
                const str = TacticalAi.unitStrength(e);
                if (e.faction === this.faction) myStr += str;
                else enemyStr += str;
            }
        }
        return [myStr, enemyStr];
    }

    /**
     * Compute spatial precompute used by `_processSendForUnit`. Captured once at the
     * Build a BFS nearest-enemy field for this faction. Each grid cell stores
     * the closest sector (by Manhattan hop count) that contains at least one
     * enemy unit. Called once per plan cycle so per-unit targeting is O(1).
     */
    _buildNearestEnemyField() {
        const grid = this.locationGrid;
        if (!grid) { this._nearestEnemySectors = null; return; }

        const gx = grid.gridsX;
        const gy = grid.gridsY;
        const n = gx * gy;
        const myFaction = this.faction;

        if (!this._nearestEnemySectors || this._nearestEnemySectors.length !== n) {
            this._nearestEnemySectors = new Array(n).fill(null);
        } else {
            this._nearestEnemySectors.fill(null);
        }
        if (!this._nearestEnemyDist || this._nearestEnemyDist.length !== n) {
            this._nearestEnemyDist = new Uint16Array(n);
        }
        this._nearestEnemyDist.fill(0xFFFF);

        const dist = this._nearestEnemyDist;
        const nearest = this._nearestEnemySectors;
        const sectors = grid.sectors;

        // BFS queue: interleaved sx, sy pairs with a head pointer to avoid shift().
        const queue = [];
        let qHead = 0;

        // Seed every cell that contains at least one enemy entity.
        for (let x = 0; x < gx; x++) {
            for (let y = 0; y < gy; y++) {
                const sector = sectors[x][y];
                const ec = sector.entityCount;
                let hasEnemy = false;
                for (let f = 1; f < ec.length; f++) {
                    if (f !== myFaction && ec[f] > 0) { hasEnemy = true; break; }
                }
                if (hasEnemy) {
                    const idx = x * gy + y;
                    dist[idx] = 0;
                    nearest[idx] = sector;
                    queue.push(x, y);
                }
            }
        }

        const NDX = [1, -1, 0, 0];
        const NDY = [0, 0, 1, -1];
        while (qHead < queue.length) {
            const cx = queue[qHead++];
            const cy = queue[qHead++];
            const d1 = dist[cx * gy + cy] + 1;
            const src = nearest[cx * gy + cy];
            for (let dir = 0; dir < 4; dir++) {
                const nx = cx + NDX[dir];
                const ny = cy + NDY[dir];
                if (nx < 0 || nx >= gx || ny < 0 || ny >= gy) continue;
                const nidx = nx * gy + ny;
                if (d1 < dist[nidx]) {
                    dist[nidx] = d1;
                    nearest[nidx] = src;
                    queue.push(nx, ny);
                }
            }
        }
    }

    /**
     * Return the precomputed nearest enemy sector for a world position, or null.
     * @param {number} x
     * @param {number} y
     * @returns {import('./util.js').Sector|null}
     */
    _lookupNearestEnemySector(x, y) {
        const grid = this.locationGrid;
        if (!grid || !this._nearestEnemySectors) return null;
        const gx = grid.clampSectorX(x);
        const gy = grid.clampSectorY(y);
        return this._nearestEnemySectors[gx * grid.gridsY + gy];
    }

    /**
     * start of a plan cycle (or per call from the legacy `sendUnits` wrapper) and
     * reused for every unit drained from the send queue this cycle.
     * @returns {{enemyNearbyBySpawner: boolean[]}}
     */
    _buildSendPrecompute() {
        this._buildNearestEnemyField();
        const enemyNearbyBySpawner = new Array(this.spawnerAnalysis.length).fill(false);
        for (let s = 0; s < this.spawnerAnalysis.length; s += 1) {
            const spawner = this.spawnerAnalysis[s].object;
            const checkRadius = spawner.capture.captureRadius * 3;
            if (this.locationGrid) {
                const sectors = this.locationGrid.getSectorsInRange(spawner.x, spawner.y, checkRadius);
                outer: for (let si = 0; si < sectors.length; si += 1) {
                    const ents = sectors[si].entities;
                    for (let i = 0; i < ents.length; i += 1) {
                        const e = ents[i];
                        if (e.faction !== this.faction && e.faction !== 0 &&
                            Util.distance(e.x, e.y, spawner.x, spawner.y) <= checkRadius) {
                            enemyNearbyBySpawner[s] = true;
                            break outer;
                        }
                    }
                }
            } else {
                for (let i = 0; i < this.entitySystem.units.length; i += 1) {
                    const other = this.entitySystem.units[i];
                    if (other.faction !== this.faction && other.faction !== 0 &&
                        Util.distance(other.x, other.y, spawner.x, spawner.y) <= checkRadius) {
                        enemyNearbyBySpawner[s] = true;
                        break;
                    }
                }
            }
        }
        return { enemyNearbyBySpawner };
    }

    /**
     * Per-unit body of the legacy `sendUnits()` loop. Safe to call from a queue
     * drained over the plan cycle: tolerant of the unit having died since being
     * queued, and reads spawner priorities live so mid-cycle worker results are
     * picked up automatically.
     * @param {Entity} unit
     * @param {{enemyNearbyBySpawner: boolean[]}} precompute
     */
    _processSendForUnit(unit, precompute) {
        if (!unit || unit.faction !== this.faction) return;
        if (unit.health && unit.health.isDestroyed()) return;

        if (this._sendChargeBerserk(unit)) return;

        this.setUnitAiDebugState(unit, "evaluating");
        if ((unit.aiLockedUntil || 0) > unit.time) {
            this.setUnitAiDebugState(unit, "ai_locked");
            return;
        }

        if (this._sendStayEngaged(unit)) return;

        const ctx = this._buildUnitSendContext(unit, precompute);
        if (this._sendHoldLockedCapture(unit, ctx)) return;
        if (this._sendGrabNeutralObjective(unit)) return;
        if (this._sendRedirectToLocalCapture(unit, ctx)) return;
        if (this._sendHoldCurrentCapture(unit, ctx)) return;
        if (this._sendDefendCurrentSpawner(unit, ctx)) return;

        const assigned = this._sendAssignBestObjective(unit, ctx);
        if (!assigned) this._sendFallbackAssignment(unit, ctx);

        if (!assigned && unit.aiDebugState === "evaluating") {
            this.setUnitAiDebugState(unit, "cruising");
        }
    }

    /**
     * Berserk units always charge the nearest enemy — skip all strategic logic.
     * @param {Entity} unit
     * @returns {boolean} true if handled
     */
    _sendChargeBerserk(unit) {
        if (!unit.berserk && !unit.auraBerserk) return false;
        const activeTarget = this.getValidEngagementTarget(unit);
        if (!activeTarget || unit.isIdle() || unit.idleTime() > 0.5) {
            this.assignEnemyShipTarget(unit);
        }
        this.setUnitAiDebugState(unit, "berserk");
        return true;
    }

    /**
     * Don't pull units away from an active fight.
     * @param {Entity} unit
     * @returns {boolean} true if handled
     */
    _sendStayEngaged(unit) {
        const activeTarget = this.getValidEngagementTarget(unit);
        if (activeTarget && !(unit.isFrozen && unit.isFrozen())) {
            const fightDistance = unit.distance(activeTarget);
            const closeCombatRange = Math.max(12, (unit.combat.attackRange || 0) * 1.25);
            if (fightDistance <= closeCombatRange) {
                this.setUnitAiDebugState(unit, "engaged");
                return true;
            }
        }
        return false;
    }

    /**
     * Snapshot of the unit's current assigned target (spawnerAnalysis[unit.aiTarget])
     * used by every decision phase below. Computed once per unit, BEFORE any
     * phase mutates unit.aiTarget.
     * @param {Entity} unit
     * @param {{enemyNearbyBySpawner: boolean[]}} precompute
     */
    _buildUnitSendContext(unit, { enemyNearbyBySpawner }) {
        const currentSa = this.spawnerAnalysis[unit.aiTarget] || null;
        const currentSpawner = currentSa ? currentSa.object : null;
        const currentCaptureRadius = currentSpawner ? currentSpawner.capture.captureRadius : 35;
        const inCurrentCaptureRadius = !!currentSpawner &&
            unit.distance(currentSpawner) <= currentCaptureRadius * 0.98;
        const nearCurrentTarget = !!currentSpawner &&
            unit.distance(currentSpawner) <= currentCaptureRadius * 1.5;
        const currentHasEnemyNearby = !!currentSpawner &&
            enemyNearbyBySpawner[unit.aiTarget] === true;
        const currentUnderAttack = !!currentSa &&
            !!currentSa.unitCounts &&
            currentSa.unitCounts[1] > 0;
        const currentCapturePct = (currentSpawner && currentSpawner.capture) ?
            (currentSpawner.capture.capturePointStatus / currentSpawner.capture.capturePoints) : 0;
        const holdAlmostCapturedTarget = !!currentSpawner &&
            currentSpawner.capturedByFaction === this.faction &&
            !currentSpawner.isFullyCaptured() &&
            currentCapturePct >= 0.8 &&
            inCurrentCaptureRadius &&
            !currentUnderAttack;
        const holdCaptureOpportunity = !!currentSpawner &&
            !currentSpawner.isFullyCaptured() &&
            inCurrentCaptureRadius &&
            !currentHasEnemyNearby &&
            !currentUnderAttack;
        return {
            enemyNearbyBySpawner,
            currentSpawner, currentCaptureRadius,
            inCurrentCaptureRadius, nearCurrentTarget,
            currentHasEnemyNearby, currentUnderAttack,
            holdAlmostCapturedTarget, holdCaptureOpportunity,
        };
    }

    /**
     * A unit locked onto an incomplete capture (aiHoldCaptureSpawner) stays
     * committed there: intercept nearby enemies, else re-center on the point.
     * Clears the lock once the spawner is fully captured.
     * @param {Entity} unit
     * @param {object} ctx
     * @returns {boolean} true if handled
     */
    _sendHoldLockedCapture(unit, ctx) {
        const lockedCaptureSpawner = unit.aiHoldCaptureSpawner;
        if (!lockedCaptureSpawner) return false;
        if (lockedCaptureSpawner.isFullyCaptured()) {
            unit.aiHoldCaptureSpawner = null;
            unit.aiHoldCapture = false;
            return false;
        }
        let lockedIndex = -1;
        for (let s = 0; s < this.spawnerAnalysis.length; s += 1) {
            if (this.spawnerAnalysis[s].object === lockedCaptureSpawner) {
                lockedIndex = s;
                break;
            }
        }
        if (lockedIndex === -1) return false;

        const lockedSa = this.spawnerAnalysis[lockedIndex];
        const lockedHasEnemyNearby = ctx.enemyNearbyBySpawner[lockedIndex] === true ||
            !!(lockedSa.unitCounts && lockedSa.unitCounts[1] > 0);
        unit.aiTarget = lockedIndex;
        unit.aiHoldCapture = true;
        if (lockedHasEnemyNearby && (unit.isIdle() || unit.idleTime() > 1)) {
            const intercepted = this.assignEnemyNearSpawnerTarget(
                unit,
                lockedCaptureSpawner,
                lockedCaptureSpawner.capture.captureRadius * 2.5
            );
            if (intercepted) {
                this.setUnitAiDebugState(unit, "defend_intercept");
                return true;
            }
        }
        if (!lockedHasEnemyNearby && unit.combat) {
            unit.combat.target = null;
        }
        if (unit.distance(lockedCaptureSpawner) > lockedCaptureSpawner.capture.captureRadius * 0.98) {
            const destination = this.getSpawnerTargetPosition(lockedCaptureSpawner);
            unit.setDestination(destination.x, destination.y);
            unit.madeAutomaticMove = false;
            this.setUnitAiDebugState(unit, "recenter_hold");
        } else {
            this.setUnitAiDebugState(unit, "hold_capture");
        }
        return true;
    }

    /**
     * Objectives (instant-capture neutrals) are scanned unconditionally — any
     * unit not in active close combat should opportunistically grab them.
     * @param {Entity} unit
     * @returns {boolean} true if handled
     */
    _sendGrabNeutralObjective(unit) {
        let nearestObjective = null;
        let nearestObjectiveDist = Number.POSITIVE_INFINITY;
        for (let oi = 0; oi < this.neutralObjectives.length; oi++) {
            const obj = this.neutralObjectives[oi];
            if (obj.isDestroyed() || obj.isFullyCaptured()) continue;
            const d = unit.distance(obj);
            const range = Math.max(200, obj.capture.captureRadius * 10);
            if (d < range && d < nearestObjectiveDist) {
                nearestObjectiveDist = d;
                nearestObjective = obj;
            }
        }
        if (nearestObjective === null) return false;
        if (unit.combat) unit.combat.target = null;
        unit.setDestination(nearestObjective.x, nearestObjective.y);
        unit.madeAutomaticMove = false;
        unit.aiHoldCapture = false;
        unit.aiHoldCaptureSpawner = null;
        this.setUnitAiDebugState(unit, "grab_objective");
        return true;
    }

    /**
     * If a unit has any nearby incomplete spawner, keep it committed there
     * until that local capture is resolved instead of rotating to far attacks.
     * @param {Entity} unit
     * @param {object} ctx
     * @returns {boolean} true if handled
     */
    _sendRedirectToLocalCapture(unit, ctx) {
        let localIncompleteIndex = -1;
        let localIncompleteDistance = Number.POSITIVE_INFINITY;
        let localIncompletePressureTier = Number.POSITIVE_INFINITY;
        const canRedirectToNearbyCapture = this.strategy !== Strategy.Defend &&
            !(ctx.currentSpawner && ctx.nearCurrentTarget && (ctx.currentHasEnemyNearby || ctx.currentUnderAttack));
        if (canRedirectToNearbyCapture) {
            for (let s = 0; s < this.spawnerAnalysis.length; s += 1) {
                const localSa = this.spawnerAnalysis[s];
                const localSpawner = localSa.object;
                if (localSpawner.isFullyCaptured()) {
                    continue;
                }
                const distanceToLocalSpawner = unit.distance(localSpawner);
                if (distanceToLocalSpawner > localSpawner.capture.captureRadius * 6.0) {
                    continue;
                }
                const localHasEnemyNearby = ctx.enemyNearbyBySpawner[s] === true ||
                    !!(localSa.unitCounts && localSa.unitCounts[1] > 0);
                const pressureTier = localHasEnemyNearby ? 1 : 0;
                if (pressureTier < localIncompletePressureTier ||
                    (pressureTier === localIncompletePressureTier &&
                        distanceToLocalSpawner < localIncompleteDistance)) {
                    localIncompletePressureTier = pressureTier;
                    localIncompleteDistance = distanceToLocalSpawner;
                    localIncompleteIndex = s;
                }
            }
        }
        if (localIncompleteIndex === -1) return false;

        const localSa = this.spawnerAnalysis[localIncompleteIndex];
        const localSpawner = localSa.object;
        const localHasEnemyNearby = ctx.enemyNearbyBySpawner[localIncompleteIndex] === true ||
            !!(localSa.unitCounts && localSa.unitCounts[1] > 0);
        unit.aiTarget = localIncompleteIndex;
        if (localHasEnemyNearby && (unit.isIdle() || unit.idleTime() > 1)) {
            const intercepted = this.assignEnemyNearSpawnerTarget(
                unit,
                localSpawner,
                localSpawner.capture.captureRadius * 2.5
            );
            if (intercepted) {
                this.setUnitAiDebugState(unit, "defend_intercept");
                return true;
            }
        }
        if (!localHasEnemyNearby && unit.combat) {
            unit.combat.target = null;
        }
        unit.aiHoldCapture = true;
        unit.aiHoldCaptureSpawner = localSpawner;
        if (unit.distance(localSpawner) > localSpawner.capture.captureRadius * 0.98) {
            const destination = this.getSpawnerTargetPosition(localSpawner);
            unit.setDestination(destination.x, destination.y);
            unit.madeAutomaticMove = false;
            this.setUnitAiDebugState(unit, "recenter_hold");
        } else {
            this.setUnitAiDebugState(unit, "hold_capture");
        }
        return true;
    }

    /**
     * Keep working the unit's current incomplete capture: lock onto it when
     * standing inside an uncontested point, or pull stragglers back into the
     * capture radius so ownership can actually progress.
     * @param {Entity} unit
     * @param {object} ctx
     * @returns {boolean} true if handled
     */
    _sendHoldCurrentCapture(unit, ctx) {
        if (ctx.holdCaptureOpportunity) {
            if (unit.combat) {
                unit.combat.target = null;
            }
            unit.aiHoldCapture = true;
            unit.aiHoldCaptureSpawner = ctx.currentSpawner;
            this.setUnitAiDebugState(unit, "hold_capture");
            return true;
        }

        // Units "holding" a point but parked outside capture radius do not contribute.
        if (ctx.currentSpawner &&
            !ctx.currentSpawner.isFullyCaptured() &&
            !ctx.inCurrentCaptureRadius &&
            !ctx.currentHasEnemyNearby &&
            !ctx.currentUnderAttack &&
            (unit.isIdle() || unit.idleTime() > 1)) {
            if (unit.combat) {
                unit.combat.target = null;
            }
            unit.aiHoldCapture = true;
            unit.aiHoldCaptureSpawner = ctx.currentSpawner;
            const destination = this.getSpawnerTargetPosition(ctx.currentSpawner);
            unit.setDestination(destination.x, destination.y);
            unit.madeAutomaticMove = false;
            this.setUnitAiDebugState(unit, "recenter_hold");
            return true;
        }
        return false;
    }

    /**
     * Defensive behaviors around the current spawner: follow it while it
     * orbits, and actively intercept enemies when it is contested.
     * @param {Entity} unit
     * @param {object} ctx
     * @returns {boolean} true if handled
     */
    _sendDefendCurrentSpawner(unit, ctx) {
        // If we are in a defensive posture and this spawner is moving,
        // keep defenders anchored to its current position.
        if (ctx.currentSpawner &&
            this.strategy !== Strategy.Attack &&
            ctx.currentSpawner.capturedByFaction === this.faction &&
            !!ctx.currentSpawner.orbit &&
            !ctx.currentHasEnemyNearby &&
            !ctx.currentUnderAttack) {
            const defendDestination = this.getSpawnerTargetPosition(ctx.currentSpawner);
            const defendDestinationDrift = Util.distance(
                unit.destX,
                unit.destY,
                defendDestination.x,
                defendDestination.y
            );
            if (defendDestinationDrift > Math.max(10, ctx.currentCaptureRadius * 0.4)) {
                unit.setDestination(defendDestination.x, defendDestination.y);
                unit.madeAutomaticMove = false;
                this.setUnitAiDebugState(unit, "defend_follow_spawner");
                return true;
            }
        }

        // If defenders are parked around a contested/attacked spawner, push them
        // to actively intercept nearby enemies instead of idling on the point.
        if (ctx.currentSpawner && ctx.nearCurrentTarget && (ctx.currentHasEnemyNearby || ctx.currentUnderAttack) &&
            (unit.isIdle() || unit.idleTime() > 1.5)) {
            const engagedNearbyEnemy = this.assignEnemyNearSpawnerTarget(
                unit,
                ctx.currentSpawner,
                ctx.currentCaptureRadius * 2.5
            );
            if (engagedNearbyEnemy) {
                this.setUnitAiDebugState(unit, "defend_intercept");
                return true;
            }
        }
        return false;
    }

    /**
     * Pick the highest-priority spawner objective and (re)assign the unit to
     * it, with hysteresis to avoid ping-ponging between nearby spawners when
     * priorities fluctuate.
     * @param {Entity} unit
     * @param {object} ctx
     * @returns {boolean} true if the unit got an assignment
     */
    _sendAssignBestObjective(unit, ctx) {
        let bestAssignedIndex = -1;
        let bestAssignedDistance = Number.POSITIVE_INFINITY;
        let bestAssignedPriority = Number.POSITIVE_INFINITY;
        for (let s = 0; s < this.spawnerAnalysis.length; s += 1) {
            const spawner = this.spawnerAnalysis[s].object;
            const spawnerHasEnemyNearby = ctx.enemyNearbyBySpawner[s] === true;

            // Never spend assignments on fully safe rear stars.
            if (spawner.capturedByFaction === this.faction &&
                spawner.isFullyCaptured() &&
                !spawnerHasEnemyNearby) {
                continue;
            }

            const unitDistanceToSpawner = unit.distance(spawner);
            const objectivePriority = this.getSpawnerObjectivePriority(spawner, spawnerHasEnemyNearby);
            if (objectivePriority < bestAssignedPriority ||
                (objectivePriority === bestAssignedPriority &&
                    unitDistanceToSpawner < bestAssignedDistance)) {
                bestAssignedPriority = objectivePriority;
                bestAssignedDistance = unitDistanceToSpawner;
                bestAssignedIndex = s;
            }
        }
        if (bestAssignedIndex === -1) return false;

        const sa = this.spawnerAnalysis[bestAssignedIndex];
        const spawner = sa.object;
        const movingTarget = this.isSpawnerMoving(spawner);
        const targetDestination = this.getSpawnerTargetPosition(spawner);
        const destinationDrift = Util.distance(
            unit.destX,
            unit.destY,
            targetDestination.x,
            targetDestination.y
        );
        const shouldTrackMovingTarget = movingTarget &&
            destinationDrift > Math.max(8, spawner.capture.captureRadius * 0.5);

        // Keep units committed while close to a target to avoid ping-ponging
        // between nearby spawners when priorities fluctuate.
        const switchingTargets = unit.aiTarget !== bestAssignedIndex;
        const forceAttackRetarget = this.strategy === Strategy.Attack &&
            switchingTargets &&
            !ctx.holdAlmostCapturedTarget;
        const idleWakeup = unit.isIdle() && unit.idleTime() > 2;
        const farFromObjective = unit.distance(spawner) > spawner.capture.captureRadius * 4;
        const stalledAwayFromAssignedObjective = !switchingTargets &&
            unit.distance(spawner) > spawner.capture.captureRadius * 1.6 &&
            (unit.isIdle() || unit.idleTime() > 1);
        const forceClosestTargetSwitch = switchingTargets &&
            !ctx.holdAlmostCapturedTarget &&
            (this.strategy === Strategy.Attack || idleWakeup || farFromObjective);
        const shouldReassign =
            (unit.madeAutomaticMove && unit.isIdle()) ||
            unit.idleTime() > 4 ||
            idleWakeup ||
            stalledAwayFromAssignedObjective ||
            (ctx.currentUnderAttack && ctx.nearCurrentTarget) ||
            shouldTrackMovingTarget ||
            forceAttackRetarget ||
            forceClosestTargetSwitch ||
            (switchingTargets && !ctx.holdAlmostCapturedTarget &&
                (!ctx.nearCurrentTarget || !ctx.currentHasEnemyNearby));

        if (shouldReassign) {
            unit.aiHoldCapture = false;
            unit.aiHoldCaptureSpawner = null;
            unit.setDestination(targetDestination.x, targetDestination.y);
            unit.madeAutomaticMove = false;
            unit.aiTarget = bestAssignedIndex;
            sa.unitsToSend -= 1;
            this.setUnitAiDebugState(unit, "objective_retarget");
            return true;
        } else if (!switchingTargets) {
            unit.aiTarget = bestAssignedIndex;
            sa.unitsToSend -= 1;
            this.setUnitAiDebugState(unit, "objective_hold");
            return true;
        }
        return false;
    }

    /**
     * Fallback: if the unit got no assignment and is idle, force it toward
     * an enemy-owned (or not-fully-captured) spawner instead of hovering.
     * @param {Entity} unit
     * @param {object} ctx
     */
    _sendFallbackAssignment(unit, ctx) {
        const shouldForce = unit.isIdle() || unit.idleTime() > 2 ||
            (ctx.currentSpawner && ctx.currentSpawner.capturedByFaction === this.faction &&
             ctx.currentSpawner.isFullyCaptured() && !ctx.currentHasEnemyNearby);
        if (!shouldForce) return;
        if (ctx.holdAlmostCapturedTarget) {
            this.setUnitAiDebugState(unit, "hold_capture");
            return;
        }
        let bestIndex = -1;
        let bestScore = Number.POSITIVE_INFINITY;

        for (let s = 0; s < this.spawnerAnalysis.length; s += 1) {
            const spawner = this.spawnerAnalysis[s].object;
            const spawnerHasEnemyNearby = ctx.enemyNearbyBySpawner[s] === true;
            if (spawner.capturedByFaction === this.faction && spawner.isFullyCaptured()) {
                continue;
            }

            // Prioritize incomplete capture opportunities before distant new attacks.
            const objectiveTier = this.getSpawnerObjectivePriority(spawner, spawnerHasEnemyNearby);
            const distanceScore = unit.distance(spawner);
            const enemyPresenceBonus = spawnerHasEnemyNearby ? -120 : 0;
            const score = objectiveTier * 10000 + distanceScore + enemyPresenceBonus;

            if (score < bestScore) {
                bestScore = score;
                bestIndex = s;
            }
        }

        if (bestIndex !== -1) {
            unit.aiHoldCapture = false;
            unit.aiHoldCaptureSpawner = null;
            const spawner = this.spawnerAnalysis[bestIndex].object;
            const destination = this.getSpawnerTargetPosition(spawner);
            unit.setDestination(destination.x, destination.y);
            unit.aiTarget = bestIndex;
            unit.madeAutomaticMove = false;
            this.setUnitAiDebugState(unit, "fallback_spawner");
        } else {
            const gotEnemy = this.assignEnemyShipTarget(unit);
            this.setUnitAiDebugState(unit, gotEnemy ? "fallback_enemy_ship" : "idle_no_target");
        }
    }

    /**
     * Synchronous batch processing of the entire faction's units. Used by tests
     * and as a one-shot equivalent to `_beginSendCycle` + drain-to-completion.
     */
    sendUnits() {
        const precompute = this._buildSendPrecompute();
        const units = this.entitySystem.units;
        for (let u = 0; u < units.length; u += 1) {
            this._processSendForUnit(units[u], precompute);
        }
    }

    /**
     * Snapshot this faction's units and build the spatial precompute so the queue
     * can be drained progressively across the plan interval. Called once per plan
     * tick from `update()`.
     */
    _beginSendCycle() {
        const all = this.entitySystem.units;
        const queue = [];
        for (let i = 0; i < all.length; i += 1) {
            if (all[i].faction === this.faction) queue.push(all[i]);
        }
        this._sendQueue = queue;
        this._sendCursor = 0;
        this._sendPrecompute = this._buildSendPrecompute();
        this._sendCycleEndTime = this.nextPlanTime;
    }

    /**
     * Process a budgeted slice of the send queue. Budget = ceil(remaining / framesLeft)
     * so the queue empties by the end of the cycle even with variable frame timing.
     * @param {number} timePassed - dt in seconds for the current frame.
     */
    _drainSendQueue(timePassed) {
        const queue = this._sendQueue;
        if (!queue || this._sendCursor >= queue.length) return;
        const remaining = queue.length - this._sendCursor;
        const dt = timePassed > 0 ? timePassed : 1 / 60;
        const timeLeft = Math.max(dt, this._sendCycleEndTime - this.time);
        const framesLeft = Math.max(1, Math.ceil(timeLeft / dt));
        const budget = Math.max(1, Math.ceil(remaining / framesLeft));
        const stop = Math.min(this._sendCursor + budget, queue.length);
        const precompute = this._sendPrecompute;
        for (let i = this._sendCursor; i < stop; i += 1) {
            this._processSendForUnit(queue[i], precompute);
        }
        this._sendCursor = stop;
    }

    /**
     * @param {Entity} unit
     * @returns {boolean}
     */
    isAggressiveMissile(unit) {
        return !!unit.mine;
    }

    /**
     * Missiles always prioritize attacking enemy/non-secure objectives.
     * @param {Entity} unit
     */
    assignAggressiveMissileTarget(unit) {
        let bestIndex = -1;
        let bestScore = Number.POSITIVE_INFINITY;

        for (let s = 0; s < this.spawnerAnalysis.length; s += 1) {
            const sa = this.spawnerAnalysis[s];
            const spawner = sa.object;

            // Prefer enemy-owned targets first, then neutral/in-progress.
            const isEnemyOwned = spawner.capturedByFaction !== this.faction && spawner.capturedByFaction !== 0;
            const isContestedOrNeutral = spawner.capturedByFaction !== this.faction || !spawner.isFullyCaptured();
            if (!isEnemyOwned && !isContestedOrNeutral) {
                continue;
            }

            const ownershipPriority = isEnemyOwned ? 0 : 500;
            const distanceScore = unit.distance(spawner);
            const score = ownershipPriority + distanceScore;

            if (score < bestScore) {
                bestScore = score;
                bestIndex = s;
            }
        }

        if (bestIndex !== -1) {
            const targetSpawner = this.spawnerAnalysis[bestIndex].object;
            const destination = this.getSpawnerTargetPosition(targetSpawner);
            unit.setDestination(destination.x, destination.y);
            unit.aiTarget = bestIndex;
            unit.madeAutomaticMove = false;
            this.setUnitAiDebugState(unit, "missile_spawner");
            return;
        }

        const gotEnemy = this.assignEnemyShipTarget(unit);
        this.setUnitAiDebugState(unit, gotEnemy ? "missile_enemy_ship" : "missile_idle");
    }

    /**
     * Fallback targeting when no valid spawner objective remains.
     * @param {Entity} unit
     */
    assignEnemyShipTarget(unit) {
        // Fast path: BFS field points us to the nearest enemy sector in O(1).
        if (this._nearestEnemySectors) {
            const sector = this._lookupNearestEnemySector(unit.x, unit.y);
            if (sector) {
                const ents = sector.entities;
                let best = null;
                let bestDistSq = Infinity;
                for (let i = 0; i < ents.length; i++) {
                    const e = ents[i];
                    if (e.faction === this.faction || e.faction === 0) continue;
                    if (!e.health || e.health.isDestroyed()) continue;
                    const dSq = Util.distSq(unit.x, unit.y, e.x, e.y);
                    if (dSq < bestDistSq) { bestDistSq = dSq; best = e; }
                }
                if (best) {
                    unit.setDestination(best.x, best.y);
                    unit.madeAutomaticMove = false;
                    return true;
                }
                // Sector was empty (units died since last plan tick) — fall through.
            }
        }

        // Fallback: spiral-out grid search or linear scan.
        const enemy = this.locationGrid
            ? findNearestEnemy(unit, this.locationGrid, this.faction)
            : this._findNearestEnemyLinear(unit);
        if (enemy) {
            unit.setDestination(enemy.x, enemy.y);
            unit.madeAutomaticMove = false;
            return true;
        }
        return false;
    }

    /**
     * O(U) linear scan fallback used only when no locationGrid is available (tests/training).
     * @param {object} unit
     * @returns {object|null}
     */
    _findNearestEnemyLinear(unit) {
        let bestEnemy = null;
        let bestDistSq = Number.POSITIVE_INFINITY;
        for (let i = 0; i < this.entitySystem.units.length; i += 1) {
            const e = this.entitySystem.units[i];
            if (e.faction === this.faction || e.faction === 0) continue;
            if (!e.health || e.health.isDestroyed()) continue;
            const dSq = Util.distSq(unit.x, unit.y, e.x, e.y);
            if (dSq < bestDistSq) { bestDistSq = dSq; bestEnemy = e; }
        }
        return bestEnemy;
    }

    /**
     * Prefer engaging enemies currently pressuring a specific spawner.
     * @param {Entity} unit
     * @param {Entity} spawner
     * @param {number} radius
     * @returns {boolean}
     */
    assignEnemyNearSpawnerTarget(unit, spawner, radius) {
        let bestEnemy = null;
        let bestDistanceSq = Number.POSITIVE_INFINITY;

        if (this.locationGrid) {
            const sectors = this.locationGrid.getSectorsInRange(spawner.x, spawner.y, radius);
            const radiusSq = radius * radius;
            for (let si = 0; si < sectors.length; si += 1) {
                const ents = sectors[si].entities;
                for (let i = 0; i < ents.length; i += 1) {
                    const e = ents[i];
                    if (e.faction === this.faction || e.faction === 0) continue;
                    if (!e.health || e.health.isDestroyed()) continue;
                    const sdx = e.x - spawner.x;
                    const sdy = e.y - spawner.y;
                    if (sdx * sdx + sdy * sdy > radiusSq) continue;
                    const udx = unit.x - e.x;
                    const udy = unit.y - e.y;
                    const distSq = udx * udx + udy * udy;
                    if (distSq < bestDistanceSq) {
                        bestDistanceSq = distSq;
                        bestEnemy = e;
                    }
                }
            }
        } else {
            for (let i = 0; i < this.entitySystem.units.length; i += 1) {
                const enemy = this.entitySystem.units[i];
                if (enemy.faction === this.faction || enemy.faction === 0) continue;
                if (!enemy.health || enemy.health.isDestroyed()) continue;
                if (Util.distance(enemy.x, enemy.y, spawner.x, spawner.y) > radius) continue;
                const udx = unit.x - enemy.x;
                const udy = unit.y - enemy.y;
                const distSq = udx * udx + udy * udy;
                if (distSq < bestDistanceSq) {
                    bestDistanceSq = distSq;
                    bestEnemy = enemy;
                }
            }
        }

        if (!bestEnemy) return false;
        unit.setDestination(bestEnemy.x, bestEnemy.y);
        unit.madeAutomaticMove = false;
        return true;
    }

    /**
     * @param {Unit} unit
     * @param {Spawner} spawner
     */
    registerUnit(unit, spawner) {
        var s;
        unit.aiTarget = 0;
        for (s = 0; s < this.spawnerAnalysis.length; s += 1) {
            if (spawner === this.spawnerAnalysis[s].object) {
                unit.aiTarget = s;
            }
        }
    }
}

export { Strategy, TacticalAi };
